import { useCallback, useState } from "react";
import { FieldDescriptor } from "../types";

export const useDataExtract = () => {
  const [extractedData, setExtractedData] = useState<Record<string, unknown>[]>([]);

  const isEmptyRow = (row: string[]) => {
    return row.map(cell => cell.trim?.()).every(cell => cell === '' || cell === null || cell === undefined);
  }

  const getFieldDescriptorsWithIndexes = (headers: string[], fieldDescriptors: FieldDescriptor[]) => {
    return fieldDescriptors.map(fieldDescriptor => ({
      ...fieldDescriptor,
      index: headers.indexOf(fieldDescriptor.name.toLowerCase().trim())
    }));
  }

  const validateMissingHeaders = (headers: string[], fieldDescriptors: FieldDescriptor[]): string | undefined => {
    const missingHeaders = fieldDescriptors.filter(fieldDescriptor => fieldDescriptor.isRequired).map(fieldDescriptor => fieldDescriptor.name).filter(header => !headers.includes(header.toLowerCase().trim()));

    if (missingHeaders.length > 0) {
      return `Missing required fields: ${missingHeaders.join(', ')}`;
    }

    return;
  }

  const validateField = (value: string, fieldDescriptor: FieldDescriptor): string | undefined => {
    for (const validator of fieldDescriptor.validators) {
      const errorMessage = validator(value);

      if (errorMessage) {
        return `${fieldDescriptor.name}: ${errorMessage}`;
      }
    }

    return;
  }

  const extractData = useCallback((data: string[][], fieldDescriptors: FieldDescriptor[]): { success: true, data: { [key in FieldDescriptor['name']]: string }[] } | { success: false, message: string } => {
    const headers = data[0].map(header => header.toLowerCase().trim());

    const missingHeadersErrorMessage = validateMissingHeaders(headers, fieldDescriptors);

    if (missingHeadersErrorMessage) {
      return {
        success: false,
        message: missingHeadersErrorMessage,
      }
    }

    const dataRows = data.slice(1);
    const fieldDescriptorsWithIndexes = getFieldDescriptorsWithIndexes(headers, fieldDescriptors);

    const payload: Record<string, unknown>[] = [];

    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i];

      if (isEmptyRow(row)) {
        continue;
      }

      const rowObject: Record<string, unknown> = {};

      for (const fieldDescriptor of fieldDescriptorsWithIndexes) {
        if (fieldDescriptor.index === -1) {
          continue;
        }

        const fieldValue = fieldDescriptor.formatValue(row[fieldDescriptor.index]);
        const errorMessage = validateField(fieldValue, fieldDescriptor);

        if (errorMessage) {
          return {
            success: false,
            message: `${errorMessage} (строка ${i + 2})`,
          }
        }

        rowObject[fieldDescriptor.name] = fieldValue;
      }

      payload.push(rowObject);
    }

    setExtractedData(payload);

    return {
      success: true,
      data: payload as { [key in FieldDescriptor[][number]['name']]: string }[],
    }
  }, []);

  return [extractData, extractedData] as const;
}